import axios from 'axios';
import {
	ChatData,
	FacebookMedia,
	FacebookPost,
	FacebookPostConversation,
} from '../types';
import { uploadFileToS3 } from './S3Service';

const baseUrl = process.env.REACT_APP_API_URL || '';

interface ApiResult<T> {
	error: boolean;
	data?: T;
	message?: unknown;
}

interface FetchFacebookPostsParams {
	page: number;
	limit: number;
	search?: string;
}

interface FetchFacebookPostsResult {
	posts: Array<FacebookPost>;
	total: number;
}

interface FacebookPostsListResponseDto {
	campaigns: Array<FacebookPostApiItem>;
	total: number;
	page: number;
	limit: number;
	total_pages: number;
}

interface FacebookPostApiMedia {
	s3Url?: string;
	url?: string;
	fileName?: string;
	file?: File;
	[key: string]: unknown;
}

interface FacebookPostApiItem {
	id?: string;
	_id?: string;
	source_id?: string;
	post_id?: string;
	campaign_name?: string;
	name?: string;
	description?: string;
	likes?: number;
	active?: boolean;
	post_url?: string;
	welcome_messages?: {
		en?: Array<string>;
		si?: Array<string>;
		ta?: Array<string>;
		english?: string;
		sinhala?: string;
		tamil?: string;
	};
	media?: Array<FacebookPostApiMedia>;
	[key: string]: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapFacebookPost(data: FacebookPostApiItem): FacebookPost {
	// welcome_messages may come back as {en:[...], si:[...], ta:[...]} arrays
	const wm = data.welcome_messages || {};
	const media = (data.media || []).map((item: FacebookPostApiMedia) => ({
		...item,
		s3Url: item.s3Url || '',
		type: (item.type as FacebookMedia['type']) ?? 'image',
	}));
	return {
		id: data.id ?? data._id ?? '',
		source_id: data.source_id ?? data.post_id ?? '',
		name: data.campaign_name ?? data.name ?? '',
		description: data.description ?? '',
		media,
		likes: data.likes,
		active: data.active ?? true,
		welcomeMessages: {
			english: (Array.isArray(wm.en) ? wm.en[0] : wm.english) ?? '',
			sinhala: (Array.isArray(wm.si) ? wm.si[0] : wm.sinhala) ?? '',
			tamil: (Array.isArray(wm.ta) ? wm.ta[0] : wm.tamil) ?? '',
		},
		postUrl: data.post_url ?? '',
	};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapFacebookPostConversation(data: any): FacebookPostConversation {
	return {
		id: data.id ?? data.conversation_id ?? data._id ?? '',
		name: data.name ?? data.customer_name ?? '',
		phoneNumber: data.phoneNumber ?? data.phone_number ?? '',
		timestamp: data.timestamp ?? data.time_stamp ?? '',
		isConsultantNeeded:
			data.isConsultantNeeded ??
			data.is_consultant_needed ??
			data.consultant_mode ??
			false,
		unreadMessageCount: data.unreadMessageCount ?? data.unread_count ?? 0,
		messages: data.messages ?? data.conversation ?? {},
	};
}

async function buildFacebookPostFormData(post: FacebookPost) {
	const uploadedMedia = await uploadFacebookPostMedia(post);
	const formData = new FormData();
	formData.append('source_id', post.source_id);
	formData.append('campaign_name', post.name);
	formData.append(
		'welcome_messages',
		JSON.stringify({
			en: [post.welcomeMessages.english],
			si: [post.welcomeMessages.sinhala],
			ta: [post.welcomeMessages.tamil],
		}),
	);
	formData.append('description', post.description ?? '');
	formData.append('post_url', post.postUrl ?? '');
	formData.append('active', 'true');
	formData.append('media', JSON.stringify(uploadedMedia));

	return formData;
}

async function uploadFacebookPostMedia(post: FacebookPost) {
	const uploadFolder = post.source_id || post.id || 'facebook-posts';

	return Promise.all(
		post.media.map(async media => {
			const s3Url = media.file
				? await uploadFileToS3(uploadFolder, media.file)
				: media.s3Url;

			return {
				type: media.type,
				s3Url,
				fileName: media.fileName ?? media.file?.name ?? '',
			};
		}),
	);
}

export async function fetchFacebookPosts({
	page,
	limit,
	search,
}: FetchFacebookPostsParams): Promise<ApiResult<FetchFacebookPostsResult>> {
	try {
		const response = await axios.get<FacebookPostsListResponseDto>(
			`${baseUrl}/social/facebook/posts`,
			{
				params: {
					page: page + 1,
					limit,
					search: search?.trim() || '',
				},
			},
		);
		return {
			error: false,
			data: {
				posts: await Promise.all(
					response.data.campaigns.map(post => mapFacebookPost(post)),
				),
				total: response.data.total,
			},
		};
	} catch (error) {
		return toApiError(error);
	}
}

export async function fetchFacebookPostConversations(
	postId: string,
): Promise<ApiResult<FacebookPostConversation[]>> {
	try {
		const response = await axios.get(
			`${baseUrl}/social/facebook/posts/${postId}/conversations`,
		);
		const conversations =
			response.data?.conversations ??
			response.data?.whatsappConversations ??
			response.data?.data ??
			response.data;

		return {
			error: false,
			data: Array.isArray(conversations)
				? conversations.map(mapFacebookPostConversation)
				: [],
		};
	} catch (error) {
		return toApiError(error);
	}
}

export async function saveFacebookPost(
	post: FacebookPost,
	isCreateMode = true,
): Promise<ApiResult<FacebookPost>> {
	try {
		const formData = await buildFacebookPostFormData(post);
		const url = isCreateMode
			? `${baseUrl}/social/facebook/posts`
			: `${baseUrl}/social/facebook/posts/${post.id}`;

		const response = await axios.put(url, formData);
		return { error: false, data: mapFacebookPost(response.data) };
	} catch (error) {
		return toApiError(error);
	}
}

export async function deleteFacebookPost(
	postId: string,
): Promise<ApiResult<string>> {
	try {
		await axios.delete(`${baseUrl}/social/facebook/posts/${postId}`);
		return { error: false, data: postId };
	} catch (error) {
		return toApiError(error);
	}
}

export async function updateFacebookBotMessage(
	postId: string,
	conversationId: string,
	date: string,
	messageIndex: number,
	message: string,
): Promise<ApiResult<FacebookPost | null>> {
	try {
		const response = await axios.patch(
			`${baseUrl}/social/facebook/posts/${postId}/conversations/${conversationId}/messages`,
			{
				date,
				messageIndex,
				message,
			},
		);
		return { error: false, data: response.data };
	} catch (error) {
		return toApiError(error);
	}
}

export async function updateFacebookConsultantMode(
	postId: string,
	conversationId: string,
	isConsultantModeOn: boolean,
): Promise<ApiResult<FacebookPost | null>> {
	try {
		const response = await axios.patch(
			`${baseUrl}/social/facebook/posts/${postId}/conversations/${conversationId}/consultant-mode`,
			{
				isConsultantModeOn,
			},
		);
		return { error: false, data: response.data };
	} catch (error) {
		return toApiError(error);
	}
}

export async function sendFacebookConsultantMessage(
	postId: string,
	conversationId: string,
	message: ChatData,
): Promise<ApiResult<FacebookPost | null>> {
	try {
		const response = await axios.post(
			`${baseUrl}/social/facebook/posts/${postId}/conversations/${conversationId}/messages`,
			message,
		);
		return { error: false, data: response.data };
	} catch (error) {
		return toApiError(error);
	}
}

function toApiError(error: unknown) {
	if (axios.isAxiosError(error)) {
		return { error: true, message: error.message };
	}

	return { error: true, message: error };
}
